import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { MatOption } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { InlineSvgComponent } from '../../../../../components/inline-svg/inline-svg.component';
import {
  FilterField,
  FilterGroup,
  FilterOperator,
  LogicalOperator,
  SortConfig
} from '../../../../../models/filter.models';
import {
  FieldKey,
  FieldKeysResponse,
  FieldValuesResponse,
  FilterBody
} from '../../../../../models/general.models';
import { AreaService } from '../../../../../services/area.service';

@Component({
  selector: 'frmg-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  imports: [
    ReactiveFormsModule,
    InlineSvgComponent,
    MatTooltip,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    MatButton
  ]
})
export class FilterComponent implements OnInit, OnChanges {
  public filterGroup: FormGroup;
  public availableFields: FieldKey[] = [];
  public fieldValuesCache: Map<string, any[]> = new Map();
  public loadingFields = false;
  public loadingValues = false;

  public filterOperatorShortLabels: Record<FilterOperator, string> = {
    [FilterOperator.EQ]: 'Equals',
    [FilterOperator.NE]: 'Not Equals',
    [FilterOperator.GT]: 'Greater Than',
    [FilterOperator.GTE]: 'Greater or Equal',
    [FilterOperator.LT]: 'Less Than',
    [FilterOperator.LTE]: 'Less or Equal',
    [FilterOperator.LIKE]: 'Like',
    [FilterOperator.ILIKE]: 'Includes',
    [FilterOperator.IN]: 'In',
    [FilterOperator.NOT_IN]: 'Not In',
    [FilterOperator.IS_NULL]: 'Is Null',
    [FilterOperator.IS_NOT_NULL]: 'Is Not Null',
    [FilterOperator.CONTAINS]: 'Contains',
    [FilterOperator.EXISTS]: 'Exists'
  };

  @Input() projectId: string;
  @Input() folderId?: string;
  @Input() filter: FilterBody;

  @Output() closeMenu = new EventEmitter<void>();
  @Output() onApply = new EventEmitter<{
    filters?: FilterField[] | FilterGroup;
    sorts?: SortConfig[];
  }>();

  constructor(
    private fb: FormBuilder,
    private areaService: AreaService
  ) {
    this.filterGroup = this.fb.group({
      orGroups: this.fb.array([this.createOrGroup()])
    });
  }

  ngOnInit(): void {
    if (this.projectId) {
      this.loadAvailableFields();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['projectId'] && this.projectId) {
      this.loadAvailableFields();
    }
    if (changes['folderId'] && this.projectId) {
      this.loadAvailableFields();
    }
  }

  /**
   * Get the FormArray of OR groups
   */
  get orGroupsArray(): FormArray {
    return this.filterGroup.get('orGroups') as FormArray;
  }

  /**
   * Get OR groups as FormGroup array
   */
  get orGroupsFormGroups(): FormGroup[] {
    return this.orGroupsArray.controls as FormGroup[];
  }

  /**
   * Get the FormArray of filters for a specific OR group
   */
  getFiltersArray(groupIndex: number): FormArray {
    const orGroup = this.orGroupsArray.at(groupIndex) as FormGroup;
    return orGroup.get('filters') as FormArray;
  }

  /**
   * Get filters as FormGroup array for a specific OR group
   */
  getFiltersFormGroups(groupIndex: number): FormGroup[] {
    return this.getFiltersArray(groupIndex).controls as FormGroup[];
  }

  /**
   * Create a new OR group (filter block)
   */
  createOrGroup(): FormGroup {
    return this.fb.group({
      filters: this.fb.array([this.createFilterRow()])
    });
  }

  /**
   * Create a new filter row
   */
  createFilterRow(): FormGroup {
    const filterRow = this.fb.group({
      field: ['', [Validators.required]],
      operator: [FilterOperator.EQ, [Validators.required]],
      value: ['']
    });

    const fieldControl = filterRow.get('field');
    if (fieldControl) {
      fieldControl.valueChanges.subscribe(() => {
        const operatorControl = filterRow.get('operator');
        if (operatorControl) {
          const fieldType = this.getFieldType(fieldControl.value || '');
          const availableOperators = this.getOperatorsForField(fieldType);
          if (availableOperators.length > 0) {
            operatorControl.setValue(availableOperators[0], {
              emitEvent: false
            });
          }
        }
        const valueControl = filterRow.get('value');
        if (valueControl) {
          valueControl.setValue('', { emitEvent: false });
          const operator = filterRow.get('operator')?.value;
          if (this.operatorRequiresValue(operator)) {
            valueControl.setValidators([Validators.required]);
          } else {
            valueControl.clearValidators();
          }
          valueControl.updateValueAndValidity({ emitEvent: false });
        }
      });
    }

    const operatorControl = filterRow.get('operator');
    if (operatorControl) {
      operatorControl.valueChanges.subscribe((operator) => {
        const valueControl = filterRow.get('value');
        if (valueControl) {
          if (this.operatorRequiresValue(operator)) {
            valueControl.setValidators([Validators.required]);
          } else {
            valueControl.clearValidators();
            valueControl.setValue('', { emitEvent: false });
          }
          valueControl.updateValueAndValidity({ emitEvent: false });
        }
      });
    }

    return filterRow;
  }

  /**
   * Add a new OR group (filter block)
   */
  addOrGroup(): void {
    this.orGroupsArray.push(this.createOrGroup());
  }

  /**
   * Remove an OR group
   */
  removeOrGroup(index: number): void {
    if (this.orGroupsArray.length > 1) {
      this.orGroupsArray.removeAt(index);
    }
  }

  /**
   * Add a filter row to a specific OR group
   */
  addFilter(groupIndex: number): void {
    const filtersArray = this.getFiltersArray(groupIndex);
    filtersArray.push(this.createFilterRow());
  }

  /**
   * Remove a filter row from a specific OR group
   */
  removeFilter(groupIndex: number, filterIndex: number): void {
    const filtersArray = this.getFiltersArray(groupIndex);
    if (filtersArray.length > 1) {
      filtersArray.removeAt(filterIndex);
    }
  }

  /**
   * Clear all filters in a specific OR group
   */
  clearGroup(groupIndex: number): void {
    const filtersArray = this.getFiltersArray(groupIndex);
    filtersArray.clear();
    filtersArray.push(this.createFilterRow());
  }

  /**
   * Clear all filters
   */
  clear(): void {
    this.orGroupsArray.clear();
    this.orGroupsArray.push(this.createOrGroup());
  }

  /**
   * Load available fields from API
   */
  loadAvailableFields(): void {
    if (!this.projectId) return;

    this.loadingFields = true;
    this.areaService
      .getFieldKeys(this.projectId, this.folderId)
      .pipe(
        catchError((error) => {
          console.error('Error loading field keys:', error);
          return of({
            columns: [],
            properties_keys: [],
            area_metadata_keys: []
          });
        })
      )
      .subscribe((response: FieldKeysResponse) => {
        const scalarTypes = [
          'string',
          'number',
          'boolean',
          'datetime',
          'integer',
          'float',
          'uuid'
        ];
        const allFields: FieldKey[] = [
          ...response.columns,
          ...response.properties_keys.map((k) => ({
            name: `properties.${k.name}`,
            type: k.type
          })),
          ...response.area_metadata_keys.map((k) => ({
            name: `area_metadata.${k.name}`,
            type: k.type
          }))
        ];

        this.availableFields = allFields.filter((field) =>
          scalarTypes.includes(field.type)
        );

        this.availableFields.sort((a, b) => a.name.localeCompare(b.name));

        this.loadingFields = false;
      });
  }

  /**
   * Get field values for a specific field (for dropdown)
   */
  getFieldValues(fieldName: string): Observable<any[]> {
    if (!this.projectId || !fieldName) {
      return of([]);
    }

    // Check cache first
    if (this.fieldValuesCache.has(fieldName)) {
      return of(this.fieldValuesCache.get(fieldName)!);
    }

    this.loadingValues = true;
    return this.areaService
      .getFieldValues(this.projectId, fieldName, this.folderId, 100, 0)
      .pipe(
        map((response: FieldValuesResponse) => {
          this.loadingValues = false;
          // Only cache if message is null (successful response)
          if (!response.message && response.values) {
            this.fieldValuesCache.set(fieldName, response.values);
          }
          return response.values || [];
        }),
        catchError((error) => {
          console.error(`Error loading values for field ${fieldName}:`, error);
          this.loadingValues = false;
          return of([]);
        })
      );
  }

  /**
   * Get operators for a field based on its type
   */
  getOperatorsForField(fieldType: string): FilterOperator[] {
    const baseOperators = [
      FilterOperator.EQ,
      FilterOperator.NE,
      FilterOperator.IS_NULL,
      FilterOperator.IS_NOT_NULL
    ];

    switch (fieldType) {
      case 'string':
        return [
          ...baseOperators,
          FilterOperator.LIKE,
          FilterOperator.ILIKE,
          FilterOperator.IN,
          FilterOperator.NOT_IN,
          FilterOperator.CONTAINS
        ];
      case 'number':
      case 'integer':
      case 'float':
        return [
          ...baseOperators,
          FilterOperator.GT,
          FilterOperator.GTE,
          FilterOperator.LT,
          FilterOperator.LTE,
          FilterOperator.IN,
          FilterOperator.NOT_IN
        ];
      case 'boolean':
        return [FilterOperator.EQ, FilterOperator.NE];
      case 'datetime':
        return [
          ...baseOperators,
          FilterOperator.GT,
          FilterOperator.GTE,
          FilterOperator.LT,
          FilterOperator.LTE
        ];
      default:
        return baseOperators;
    }
  }

  /**
   * Check if operator requires a value
   */
  operatorRequiresValue(operator: FilterOperator): boolean {
    return (
      operator !== FilterOperator.IS_NULL &&
      operator !== FilterOperator.IS_NOT_NULL
    );
  }

  /**
   * Get field type by field name
   */
  getFieldType(fieldName: string): string {
    const field = this.availableFields.find((f) => f.name === fieldName);
    return field?.type || 'string';
  }

  /**
   * Convert value to appropriate type based on field type
   */
  convertValueToType(value: any, fieldType: string): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    if (Array.isArray(value)) {
      return value.map((v) => this.convertSingleValueToType(v, fieldType));
    }

    return this.convertSingleValueToType(value, fieldType);
  }

  /**
   * Convert a single value to appropriate type
   */
  private convertSingleValueToType(value: any, fieldType: string): any {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    switch (fieldType) {
      case 'number':
      case 'integer':
      case 'float':
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (fieldType === 'integer') {
            const intValue = parseInt(trimmed, 10);
            return isNaN(intValue) ? trimmed : intValue;
          } else {
            const floatValue = parseFloat(trimmed);
            return isNaN(floatValue) ? trimmed : floatValue;
          }
        }
        return typeof value === 'number' ? value : Number(value);

      case 'boolean':
        if (typeof value === 'string') {
          const lower = value.toLowerCase().trim();
          if (lower === 'true' || lower === '1') return true;
          if (lower === 'false' || lower === '0') return false;
        }
        return Boolean(value);

      case 'string':
      case 'datetime':
      case 'uuid':
      case 'json':
      case 'geometry':
      default:
        return typeof value === 'string' ? value : String(value);
    }
  }

  /**
   * Apply filters
   */
  apply(): void {
    if (this.filterGroup.invalid) {
      return;
    }

    const orGroups = this.orGroupsArray.value;
    const filterGroups: FilterField[][] = [];

    for (const orGroup of orGroups) {
      const filters: FilterField[] = [];
      for (const filterRow of orGroup.filters) {
        if (filterRow.field && filterRow.operator) {
          if (
            this.operatorRequiresValue(filterRow.operator) &&
            !filterRow.value
          ) {
            continue;
          }

          let processedValue: any = filterRow.value || null;

          const fieldType = this.getFieldType(filterRow.field);

          if (
            (filterRow.operator === FilterOperator.IN ||
              filterRow.operator === FilterOperator.NOT_IN) &&
            typeof processedValue === 'string' &&
            processedValue.trim()
          ) {
            processedValue = processedValue
              .split('|')
              .map((v: string) => v.trim())
              .filter((v: string) => v.length > 0);
          }

          processedValue = this.convertValueToType(processedValue, fieldType);

          const filter: FilterField = {
            field: filterRow.field,
            operator: filterRow.operator as FilterOperator,
            value: processedValue
          };
          filters.push(filter);
        }
      }
      if (filters.length > 0) {
        filterGroups.push(filters);
      }
    }

    let resultFilters: FilterField[] | FilterGroup | undefined;

    if (filterGroups.length === 0) {
      resultFilters = undefined;
    } else if (filterGroups.length === 1) {
      resultFilters = filterGroups[0];
    } else {
      const orConditions: FilterField[] = [];
      for (const andGroup of filterGroups) {
        if (andGroup.length === 1) {
          orConditions.push(andGroup[0]);
        } else {
          const andGroupObj: FilterGroup = {
            operator: LogicalOperator.AND,
            conditions: andGroup
          };
          orConditions.push(andGroupObj as any);
        }
      }
      resultFilters = {
        operator: LogicalOperator.OR,
        conditions: orConditions
      } as FilterGroup;
    }

    this.onApply.emit({
      filters: resultFilters
    });
  }
}
